import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Search, Download, Plus, Trash2, Paperclip, FileText, Link2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Partner() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meta, setMeta] = useState({ brands: [], doc_types: [], equipment_types: [] });
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [brand, setBrand] = useState("all");
  const [docType, setDocType] = useState("all");
  const [equip, setEquip] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: "", brand: "Outra", equipment: "todos", doc_type: "manual", description: "", file_url: "", file_name: "", external_url: "" });

  useEffect(() => {
    api.get("/partner/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  const loadDocs = () => {
    api.get("/partner/docs", {
      params: { brand: brand === "all" ? "" : brand, doc_type: docType === "all" ? "" : docType, equipment: equip === "all" ? "" : equip, q: qDebounced },
    }).then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 400); return () => clearTimeout(t); }, [q]);
  useEffect(() => { loadDocs(); }, [brand, docType, equip, qDebounced]);

  const typeName = (id) => meta.doc_types.find((t) => t.id === id)?.name || id;
  const equipName = (id) => meta.equipment_types.find((t) => t.id === id)?.name || id;

  const uploadDoc = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo maior que 10 MB."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", "partner");
    try {
      const { data } = await api.post("/files/upload", fd);
      setForm((f) => ({ ...f, file_url: data.url, file_name: data.name, external_url: "" }));
      toast.success("Arquivo anexado.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/partner/docs", {
        title: form.title, brand: form.brand, equipment: form.equipment, doc_type: form.doc_type,
        description: form.description, file_url: form.file_url, external_url: form.external_url,
      });
      toast.success(data.message);
      setShowForm(false);
      setForm({ title: "", brand: "Outra", equipment: "todos", doc_type: "manual", description: "", file_url: "", file_name: "", external_url: "" });
      loadDocs();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const downloadDoc = async (d) => {
    try {
      const { data } = await api.post(`/partner/docs/${d.id}/download`);
      window.open(data.url, "_blank", "noopener");
      loadDocs();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Remover "${d.title}" da biblioteca?`)) return;
    try {
      await api.delete(`/partner/docs/${d.id}`);
      toast.success("Documento removido.");
      loadDocs();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="partner-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-sky-600" /> GEPR Partner <Badge className="bg-amber-100 text-amber-700 border-amber-200">BETA</Badge>
        </h1>
        <p className="text-sm text-slate-500">Biblioteca técnica: manuais, tabelas de erro e catálogos de fabricantes</p>
      </div>

      {isAdmin && !showForm && (
        <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => setShowForm(true)} data-testid="pd-new-btn">
          <Plus className="h-4 w-4 mr-2" /> Publicar documento técnico
        </Button>
      )}

      {isAdmin && showForm && (
        <Card className="border-sky-300 bg-sky-50" data-testid="pd-form-card">
          <CardHeader><CardTitle className="text-base">Novo documento técnico</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={publish} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Título *</Label>
                <Input className="h-12 bg-white" data-testid="pd-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Tabela de erros — Linha Inverter LG Dual" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Marca</Label>
                  <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                    <SelectTrigger className="h-12 bg-white" data-testid="pd-brand"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tipo de documento</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                    <SelectTrigger className="h-12 bg-white" data-testid="pd-doctype"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.doc_types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Equipamento</Label>
                  <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}>
                    <SelectTrigger className="h-12 bg-white" data-testid="pd-equipment"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.equipment_types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Descrição</Label>
                <Textarea rows={2} className="bg-white" data-testid="pd-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} />
              </div>
              <div className="space-y-1.5">
                <Label>Arquivo (imagem ou PDF, máx. 10 MB) ou link externo</Label>
                {form.file_url ? (
                  <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center justify-between" data-testid="pd-file-attached">
                    <span className="truncate flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> {form.file_name}</span>
                    <button type="button" className="text-red-500 ml-2 shrink-0" onClick={() => setForm({ ...form, file_url: "", file_name: "" })}>remover</button>
                  </p>
                ) : (
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-white rounded-lg p-3 cursor-pointer text-sm text-slate-600 hover:border-sky-400 transition-colors" data-testid="pd-upload">
                    <Paperclip className="h-4 w-4" /> Anexar arquivo
                    <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { uploadDoc(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                )}
                {!form.file_url && (
                  <Input className="h-12 bg-white" data-testid="pd-exturl" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="ou cole o link externo (https://...)" />
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="pd-submit">{sending ? "Publicando..." : "Publicar documento"}</Button>
                <Button type="button" variant="outline" className="h-12" onClick={() => setShowForm(false)} data-testid="pd-cancel">Cancelar</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-3.5 text-slate-400" />
          <Input className="h-12 pl-9" placeholder="Buscar por título ou descrição..." data-testid="partner-search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="h-11" data-testid="partner-brand"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as marcas</SelectItem>
              {meta.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="h-11" data-testid="partner-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {meta.doc_types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={equip} onValueChange={setEquip}>
            <SelectTrigger className="h-11" data-testid="partner-equip"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos equipamentos</SelectItem>
              {meta.equipment_types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {docs.length === 0 && (
        <Card className="border-slate-200"><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="no-docs">
          Nenhum documento encontrado com esses filtros.
        </CardContent></Card>
      )}
      {docs.map((d) => (
        <Card key={d.id} className="border-slate-200" data-testid={`doc-card-${d.id}`}>
          <CardContent className="p-4 flex items-start gap-3">
            <div className="bg-sky-100 rounded-lg p-2.5 shrink-0">
              {d.has_file ? <FileText className="h-5 w-5 text-sky-700" /> : <Link2 className="h-5 w-5 text-sky-700" />}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-medium text-sm">{d.title}</p>
              <p className="text-xs text-slate-500">
                <Badge variant="outline" className="text-[10px] mr-1.5">{typeName(d.doc_type)}</Badge>
                {d.brand} · {equipName(d.equipment)} · {d.downloads} downloads · {fmtDate(d.created_at)}
              </p>
              {d.description && <p className="text-sm text-slate-600 line-clamp-2">{d.description}</p>}
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="h-10" onClick={() => downloadDoc(d)} data-testid={`doc-download-${d.id}`}>
                <Download className="h-4 w-4 mr-1" /> Abrir
              </Button>
              {isAdmin && (
                <Button size="sm" variant="ghost" className="h-8 text-red-500 hover:bg-red-50" onClick={() => removeDoc(d)} data-testid={`doc-del-${d.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
